import {Main} from '../../components/main/main.tsx';
import {useAppDispatch, useAppSelector} from '../../store/store.ts';
import {useIsNavigatingOrPending} from '../../support/use_is_navigating_or_pending.ts';
import {useCallback, useEffect, useState} from 'react';
import {fetchReview} from '../../store/slices/review_slice.ts';
import {ReviewSummary} from './components/review_summary.tsx';
import {PageContent} from '../../components/page/page_content.tsx';
import {PageTitle} from '../../components/page/page_title.tsx';
import {Page} from '../../components/page/page.tsx';
import {__} from '../../support/lang/translations.ts';
import {ReviewItem} from './components/review_item.tsx';
import {CardGrid} from '../../components/card/card_grid.tsx';
import {Card} from '../../components/card/card.tsx';
import {FlexRow} from '../../components/utility/flex_row.tsx';
import {Alignment} from '../../enums/alignment.ts';
import {Button} from '../../components/button/button.tsx';
import {Spacer} from '../../components/utility/spacer.tsx';

export function Reviews() {
    const dispatch = useAppDispatch();
    const reviewData = useAppSelector((state) => state.reviews);
    const isLoading = useIsNavigatingOrPending((state) => state.reviews.status);

    const [page, setPageNumber] = useState(1);
    const lastPage: number = reviewData.review?.numberOfPages ?? 1;

    const scrollTop = () => window.scrollTo({top: 0, behavior: 'smooth'});

    useEffect(() => {
        dispatch(
            fetchReview({
                page: page,
            }),
        );
        scrollTop();
    }, [dispatch, page]);

    const onLoadNextPageCallback = useCallback(() => {
        dispatch(
            fetchReview({
                page: page + 1,
            }),
        );
        setPageNumber(page + 1);
    }, [dispatch, page]);

    const onLoadPreviousPageCallback = useCallback(() => {
        dispatch(
            fetchReview({
                page: page - 1,
            }),
        );
        setPageNumber(page - 1);
    }, [dispatch, page]);

    return (
        <Main>
            <Page isLoading={isLoading}>
                <PageTitle>
                    <h1>{__('reviews.title')}</h1>
                </PageTitle>
                <PageContent>
                    <p className="text-center">{__('reviews.intro')}</p>
                </PageContent>
                <PageContent wide={true}>
                    <Card style={{marginBottom: 0}}>
                        <ReviewSummary
                            average12MonthsRating={reviewData.review?.average12MonthsRating ?? 0}
                            averageRating={reviewData.review?.averageRating ?? 0}
                            numberOfReviews={reviewData.review?.numberOfReviews ?? 0}
                            numberOfReviews12Months={reviewData.review?.numberOfReviews12Months ?? 0}
                            percentageRecommended={reviewData.review?.percentageRecommended ?? 0}
                            reviewUrl={reviewData.review?.reviewUrl ?? null}
                        />
                    </Card>
                </PageContent>
                <PageContent wide={true}>
                    <CardGrid columns={2}>
                        {reviewData.review?.reviewList?.map((review, key: number) => (
                            <ReviewItem key={key} review={review}></ReviewItem>
                        ))}
                    </CardGrid>
                </PageContent>

                <PageContent wide={true}>
                    <FlexRow>
                        {page > 1 && (
                            <Button type="button" loading={isLoading} onClick={() => onLoadPreviousPageCallback()}>
                                <i className={isLoading ? 'fa-light fa-refresh' : 'fa-light fa-chevron-left'} />
                                {__('reviews.buttons.previous')}
                            </Button>
                        )}
                        <Spacer />
                        {page < lastPage && (
                            <Button
                                iconPosition={Alignment.End}
                                type="button"
                                loading={isLoading}
                                onClick={() => onLoadNextPageCallback()}
                            >
                                <i className={isLoading ? 'fa-light fa-refresh' : 'fa-light fa-chevron-right'} />
                                {__('reviews.buttons.next')}
                            </Button>
                        )}
                    </FlexRow>
                </PageContent>
            </Page>
        </Main>
    );
}
